import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';

import css from './OrderBreakdown.module.css';

/**
 * A component that renders multiple items from a multi-item purchase
 * with their associated shipping fees displayed underneath each item
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {Array} props.allItems - The original items with full listing data
 * @param {boolean} props.hasFreeShipping - Whether franco threshold is met (free shipping)
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemMultiItemMaybe = props => {
  const { lineItems, allItems, hasFreeShipping, intl } = props;

  // Find all line items that are part of a multi-item purchase
  // They have codes like: line-item/item-listing1, line-item/item-listing2, etc.
  const multiItems = lineItems.filter(
    item => 
      item.code && 
      item.code.startsWith('line-item/item-listing') && 
      !item.reversal &&
      item.includeFor &&
      item.includeFor.includes('customer')
  );

  if (multiItems.length === 0) {
    return null;
  }

  // Find all shipping fee line items for multi-item purchases
  const shippingFeeItems = lineItems.filter(
    item => 
      item.code && 
      (item.code.startsWith('line-item/shipping-fee-listing') || 
       item.code.match(/shipping-fee.*listing\d+$/)) &&
      !item.reversal
  );

  return (
    <>
      {multiItems.map((item, idx) => {
        // Extract listing index from code (e.g., "line-item/item-listing1" -> 1)
        const match = item.code.match(/listing(\d+)$/);
        const listingIndex = match ? parseInt(match[1], 10) - 1 : idx;
        const listingNumber = match ? parseInt(match[1], 10) : idx + 1;
        
        // Get product name from multiple sources (in order of preference):
        // 1. From allItems array (passed from CheckoutPage or TransactionPage)
        // 2. From API metadata (may be preserved in completed transactions)
        // 3. Generic fallback
        let productName = 'Article';
        
        if (allItems && Array.isArray(allItems) && allItems[listingIndex]) {
          const listingData = allItems[listingIndex];
          productName = listingData.listing?.attributes?.title || 
                       listingData.listing?.attributes?.publicData?.title ||
                       `Article ${listingIndex + 1}`;
        } else if (item.metadata?.productName) {
          productName = item.metadata.productName;
        } else if (item.metadata?.listingTitle) {
          productName = item.metadata.listingTitle;
        } else if (item.lineItemData?.listingTitle) {
          productName = item.lineItemData.listingTitle;
        } else {
          productName = `Article ${idx + 1}`;
        }
        
        const quantity = item.quantity || 1;
        const unitPrice = item.unitPrice ? formatMoney(intl, item.unitPrice) : null;
        const lineTotal = item.lineTotal ? formatMoney(intl, item.lineTotal) : null;

        // Find the corresponding shipping fee for this item
        const correspondingShippingFee = shippingFeeItems.find(
          shippingItem => 
            shippingItem.code.includes(`listing${listingNumber}`)
        );
        const shippingAmount =
          correspondingShippingFee?.lineTotal?.amount ??
          correspondingShippingFee?.unitPrice?.amount ??
          null;
        const isFreeShippingFromLineItem =
          typeof shippingAmount === 'number' && shippingAmount <= 0;
        const shouldRenderFreeShipping =
          typeof shippingAmount === 'number' ? isFreeShippingFromLineItem : hasFreeShipping;

        return (
          <React.Fragment key={item.code}>
            <div className={css.lineItem}>
              <span className={css.itemLabel}>
                {quantity > 1 ? `${quantity}x ` : '1x '}{productName}
                {unitPrice && ` à ${unitPrice}`}
              </span>
              <span className={css.itemValue}>{lineTotal}</span>
            </div>
            {correspondingShippingFee && (
              <div className={css.lineItemIndented}>
                <span className={css.itemLabel}>
                  Frais de livraison
                </span>
                <span className={css.itemValue}>
                  {shouldRenderFreeShipping ? (
                    <span style={{ color: 'var(--colorSuccess)', fontWeight: 'var(--fontWeightSemiBold)' }}>
                      <FormattedMessage id="OrderBreakdown.freeShipping" />
                    </span>
                  ) : (
                    formatMoney(intl, correspondingShippingFee.lineTotal)
                  )}
                </span>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default LineItemMultiItemMaybe;

